<script setup lang="ts">
import { PLabel } from '@ui/components/core/p-label/index.js';
import { PSwitch, type PSwitchProps } from '../p-switch/index.js';
import { cn } from '@ui/lib/utils.js';
import { type SwitchRootEmits, useForwardPropsEmits } from 'reka-ui';
import { reactiveOmit } from '@vueuse/core';
import type { HTMLAttributes } from 'vue';

interface PRichSwitchProps extends PSwitchProps {
    class?: HTMLAttributes['class'];
    flip?: boolean;
}

const props = defineProps<PRichSwitchProps>();
const emits = defineEmits<SwitchRootEmits>();
const delegatedProps = reactiveOmit(props, 'class', 'flip');

const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
    <PLabel
        :for="props.id"
        :class="
            cn(
                'group',
                'hover:bg-card-primary-hover',
                'flex',
                flip ? 'flex-row-reverse' : '',
                'items-start',
                'gap-3',
                'rounded',
                'border',
                'border-border-default',
                'p-3',
                'has-aria-checked:bg-card-primary-active',
                'aria-invalid:border-border-destructive',
                'aria-invalid:hover:bg-card-error-hover',
                'aria-invalid:has-aria-checked:bg-card-error-active',
                'aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
            )
        "
        :aria-disabled="props.disabled"
    >
        <PSwitch v-bind="forwarded" :aria-invalid="$attrs['aria-invalid']" :class="cn('mt-0.5', props.class)" />
        <div class="grid grow gap-1.5 font-normal">
            <slot />
        </div>
    </PLabel>
</template>
